import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { ICellEditorParams } from 'ag-grid-community';

import { CustomAutocomplete } from '@/components/common/custom-autocomplete';

import { SheetState } from '../../hooks/use-bulk-donor-scheduling-store';

export interface AutocompleteCellEditorProps extends ICellEditorParams {
  context: SheetState;
  searchFunction: (query: string) => Promise<string[]>;
  displayFunction?: (value: string) => string;
}

export const AutocompleteCellEditor = forwardRef(
  (props: AutocompleteCellEditorProps, ref) => {
    const {
      value,
      context,
      searchFunction,
      displayFunction,
      rowIndex,
      colDef,
    } = props;
    const [inputValue, setInputValue] = useState<string>(value || '');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [fuzzySuggestions, setFuzzySuggestions] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      getValue: () => inputValue,
      isCancelAfterEnd: () => false,
      isPopup: () => true,
    }));

    useEffect(() => {
      const fieldName = colDef?.field;
      if (rowIndex !== undefined && fieldName && 'messages' in context) {
        const message = context.messages?.[`${rowIndex}.${fieldName}`];
        if (message?.suggestions && message.suggestions.length > 0) {
          setFuzzySuggestions(message.suggestions);
          setSuggestions(message.suggestions);
        }
      }
    }, [rowIndex, colDef, context]);

    const handleSearch = useCallback(
      async (query: string) => {
        try {
          const searchResults = await searchFunction(query);

          // If user is actively typing, prioritize search results
          // Only show fuzzy suggestions if there are no search results
          if (searchResults.length > 0) {
            // Show search results with fuzzy suggestions at the top (if they match)
            const fuzzySet = new Set(fuzzySuggestions);
            const matchingFuzzy = fuzzySuggestions.filter((fs) =>
              fs.toLowerCase().includes(query.toLowerCase()),
            );
            const uniqueSearchResults = searchResults.filter(
              (r) => !fuzzySet.has(r),
            );
            const combined = [...matchingFuzzy, ...uniqueSearchResults];
            setSuggestions(combined);
          } else {
            // No search results, fall back to fuzzy suggestions
            setSuggestions(fuzzySuggestions);
          }
        } catch (error) {
          console.error('[AutocompleteCellEditor] Error searching:', error);
          setSuggestions(fuzzySuggestions);
        }
      },
      [searchFunction, fuzzySuggestions],
    );

    const itemTemplate = (item: string) => {
      return displayFunction ? displayFunction(item) : item;
    };

    return (
      <CustomAutocomplete
        ref={inputRef}
        value={inputValue}
        suggestions={suggestions}
        onSearch={handleSearch}
        onChange={setInputValue}
        onSelect={setInputValue}
        itemTemplate={itemTemplate}
        showDropdownButton
        autoFocus
        minLength={1}
        className="w-full"
        inputClassName="w-full p-1 text-sm"
        panelClassName="text-sm"
      />
    );
  },
);

AutocompleteCellEditor.displayName = 'AutocompleteCellEditor';
